import React, { useEffect, useState } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardImg,
  CardSubtitle,
  CardText,
  CardTitle,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  Pagination,
  PaginationItem,
  PaginationLink,
  Row,
  TabContent,
  TabPane,
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { Triangle } from "react-loader-spinner";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import DeleteModal from "../../Components/Common/DeleteModal";
import { imageBaseApi } from "../../utilis/imageHelper";
import MediaModal from "../PagesSection/Component/MediaModal";
import classNames from "classnames";
import ColorPicker from "../../Components/ColorPicker/ColorPicker";
import BannerService from "../../services/KangarooServices/BannerService";

const BannerSection = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(9);
  const [selectedMedia, setSelectedMedia] = useState(null);
  console.log("🚀 ~ BannerSection ~ selectedMedia:", selectedMedia);
  // Selection
  const [selectedItems, setSelectedItems] = useState([]);
  const [isSelectAll, setIsSelectAll] = useState(false);

  // Modals
  const [isOpenDelete, setIsOpenDelete] = useState(false);
  const [selectedDeleteItem, setSelectedDeleteItem] = useState(null);
  const [isOpenBulkEdit, setIsOpenBulkEdit] = useState(false);
  const [isMediaOpen, setIsMediaOpen] = useState(false);

  const toggleMedia = () => setIsMediaOpen(!isMediaOpen);
  // For individual edits inside bulk modal
  const [editForms, setEditForms] = useState({}); // { id: { title: "...", ... } }

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const result = await BannerService.getdata({ page: 1, pageSize: 100 });
      setData(result || []);
    } catch (error) {
      toast.error("Failed to fetch home sections");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Filtering & Pagination
  const filteredSections = data.filter((section) => {
    const term = searchTerm.toLowerCase();
    return (
      section.title?.toLowerCase().includes(term) ||
      section.description?.toLowerCase().includes(term) ||
      section.buttonText?.toLowerCase().includes(term)
    );
  });

  const totalPages = Math.ceil(filteredSections.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const paginatedData = filteredSections.slice(
    startIndex,
    startIndex + pageSize
  );

  useEffect(() => {
    if (paginatedData.length === 0) {
      setIsSelectAll(false);
    } else {
      setIsSelectAll(
        paginatedData.every((item) => selectedItems.includes(item.id))
      );
    }
  }, [selectedItems, paginatedData]);

  const handleSelectItem = (id) => {
    setSelectedItems((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  // Add this function to handle media selection for each item
  const handleMediaChange = (id, media) => {
    setEditForms((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        mediaId: media?.id || null, // store media id for payload
        mediaUrl: media?.url || null, // store media URL to show preview
        mediaName: media?.name || "", // optional, for display
        mediaSize: media?.size || 0,
      },
    }));
  };

  const handleSelectAll = () => {
    if (isSelectAll) {
      setSelectedItems([]);
    } else {
      setSelectedItems(paginatedData.map((item) => item.id));
    }
    setIsSelectAll(!isSelectAll);
  };

  const openBulkEdit = () => {
    if (selectedItems.length === 0) {
      toast.error("Please select at least one section");
      return;
    }

    // Pre-fill edit forms with current data
    const initialForms = {};
    selectedItems.forEach((id) => {
      const item = data.find((d) => d.id === id);
      if (item) {
        initialForms[id] = {
          title: item.title || "",
          subTitle: item.subTitle || "",
          titleColor: item.titleColor || "",
          hasButton: item.hasButton || true,

          buttonText: item.buttonText || "",
          buttonColor: item.buttonColor || "",
          buttonUrl: item.buttonUrl || "",
          mediaId: item?.media?.id,
        };
      }
    });
    setEditForms(initialForms);
    setIsOpenBulkEdit(true);
  };

  const handleFormChange = (id, field, value) => {
    setEditForms((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field]: value,
      },
    }));
  };

  const handleSaveAll = async () => {
    try {
      const updates = Object.keys(editForms).map((id) => ({
        id,
        title: editForms[id]?.title || "",
        subTitle: editForms[id]?.subTitle || "",
        buttonColor: editForms[id]?.buttonColor || "",
        titleColor: editForms[id]?.titleColor || "",
        hasButton:
          editForms[id]?.hasButton === undefined
            ? true
            : editForms[id]?.hasButton,
        buttonText: editForms[id]?.buttonText || "",
        buttonUrl: editForms[id]?.buttonUrl || "",
        mediaId: editForms[id]?.mediaId || null,
      }));

      await BannerService.bulkUpdateMany(updates); // Send array payload

      toast.success(`Successfully updated ${updates.length} section(s)`);
      fetchData();
      setSelectedItems([]);
      setIsOpenBulkEdit(false);
      setEditForms({});
    } catch (error) {
      toast.error("Some updates failed");
      console.error(error);
    }
  };

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
    setSelectedItems([]);
    setIsSelectAll(false);
  };

  const selectedData = data.filter((item) => selectedItems.includes(item.id));
  const [activeTab, setActiveTab] = useState(selectedData[0]?.id || null);

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="Banner Sections" pageTitle="Content Management" />

        {/* Bulk Actions Bar */}
        {selectedItems.length > 0 && (
          <Row className="mb-3">
            <Col>
              <div className="alert alert-info d-flex align-items-center py-2 px-3 mb-0 gap-3">
                <strong>{selectedItems.length} section(s) selected</strong>
                <Button color="primary" size="sm" onClick={openBulkEdit}>
                  Edit Selected
                </Button>
                {/* You can add Bulk Delete here later */}
              </div>
            </Col>
          </Row>
        )}

        <Row className="mb-4 align-items-center">
          <Col md={6} xs={12}>
            <div className="d-flex align-items-center gap-3">
              {/* <Button
                color="success"
                onClick={() => navigate("/homesection-form")}
              >
                <i className="ri-add-line align-bottom me-1"></i> Add BannerSection
                Section
              </Button> */}

              {paginatedData.length > 0 && (
                <div className="form-check">
                  <Input
                    type="checkbox"
                    className="form-check-input"
                    checked={isSelectAll}
                    onChange={handleSelectAll}
                  />
                  <Label className="form-check-label fw-medium">
                    Select All
                  </Label>
                </div>
              )}
            </div>
          </Col>

          <Col md={6} xs={12}>
            <div className="search-box ms-md-auto">
              <Input
                type="text"
                placeholder="Search banner sections..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
              <i className="ri-search-line search-icon"></i>
            </div>
          </Col>
        </Row>

        {isLoading ? (
          <div className="text-center my-5">
            <Triangle visible height="80" width="80" color="#405189" />
          </div>
        ) : (
          <>
            <Row>
              {paginatedData.map((item) => (
                <Col sm={6} xl={4} key={item.id} className="mb-4">
                  <Card
                    className="border shadow-sm h-100 position-relative hover-lift"
                    onClick={() => handleSelectItem(item.id)}
                  >
                    <Input
                      type="checkbox"
                      className="position-absolute"
                      style={{ top: "10px", left: "10px", zIndex: 10 }}
                      checked={selectedItems.includes(item.id)}
                      // prevent checkbox click from bubbling up to card click
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => handleSelectItem(item.id)}
                    />

                    <div style={{ height: "200px", overflow: "hidden" }}>
                      <CardImg
                        top
                        src={`${imageBaseApi}${item?.media?.url}`}
                        alt={item.slug}
                        style={{
                          objectFit: "cover",
                          width: "100%",
                          height: "100%",
                        }}
                      />
                      {/* <Badge
                        color="primary"
                        className="position-absolute top-0 end-0 m-2"
                      >
                        Order: {item.orderIndex || "-"}
                      </Badge> */}
                    </div>

                    <CardBody>
                      <small className="mb-2 text-muted">
                        {item?.slug || "Untitled"}
                      </small>
                      <h5
                        className="mb-2"
                        style={{ color: `${item?.titleColor}` }}
                      >
                        {item?.title}
                      </h5>
                      <h6
                        className="mb-2"
                        style={{
                          display: "-webkit-box",
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {item?.subTitle}
                      </h6>
                    </CardBody>
                    <CardFooter>
                      {item?.hasButton === true && (
                        <div className="mt-2">
                          <Button
                            className="btn"
                            style={{
                              backgroundColor: `${item?.buttonColor}`,
                              borderColor: `${item?.buttonColor}`,
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (item?.buttonUrl) window.open(item.buttonUrl);
                            }}
                          >
                            {item?.buttonText}
                          </Button>
                        </div>
                      )}
                    </CardFooter>
                  </Card>
                </Col>
              ))}
            </Row>

            {filteredSections.length === 0 && (
              <div className="text-center py-5">
                <i className="ri-inbox-line display-4 text-muted"></i>
                <p className="text-muted mt-3">
                  {searchTerm
                    ? "No matching sections found"
                    : "No home sections yet"}
                </p>
              </div>
            )}

            {totalPages > 1 && (
              <div className="d-flex justify-content-center mt-4">
                <Pagination>
                  <PaginationItem disabled={currentPage === 1}>
                    <PaginationLink
                      previous
                      onClick={() => handlePageChange(currentPage - 1)}
                    />
                  </PaginationItem>
                  {[...Array(totalPages)].map((_, i) => (
                    <PaginationItem key={i} active={currentPage === i + 1}>
                      <PaginationLink onClick={() => handlePageChange(i + 1)}>
                        {i + 1}
                      </PaginationLink>
                    </PaginationItem>
                  ))}
                  <PaginationItem disabled={currentPage === totalPages}>
                    <PaginationLink
                      next
                      onClick={() => handlePageChange(currentPage + 1)}
                    />
                  </PaginationItem>
                </Pagination>
              </div>
            )}
          </>
        )}
      </Container>

      {/* Single Delete Modal */}
      <DeleteModal
        show={isOpenDelete}
        onCloseClick={() => setIsOpenDelete(false)}
        onDeleteClick={async () => {
          try {
            await BannerService.delete(selectedDeleteItem.id);
            toast.success("Section deleted");
            fetchData();
          } catch (err) {
            toast.error("Delete failed");
          } finally {
            setIsOpenDelete(false);
            setSelectedDeleteItem(null);
          }
        }}
      />

      {/* Bulk Individual Edit Modal */}
      <Modal
        isOpen={isOpenBulkEdit}
        toggle={() => setIsOpenBulkEdit(false)}
        size="xl"
        scrollable
      >
        <ModalHeader toggle={() => setIsOpenBulkEdit(false)}>
          Edit {selectedData?.length} Section(s) Individually
        </ModalHeader>
        <ModalBody>
          <p className="text-muted small mb-4">
            Edit each section below independently. Changes will be saved when
            you click "Save All".
          </p>

          {/* Tabs */}
          <Nav tabs>
            {selectedData?.map((item) => (
              <NavItem key={item.id} className="cursor-pointer">
                <NavLink
                  className={classNames({ active: activeTab === item.id })}
                  onClick={() => setActiveTab(item.id)}
                >
                  {item?.slug || `Section ${item.id}`}
                </NavLink>
              </NavItem>
            ))}
          </Nav>

          <TabContent activeTab={activeTab} className="">
            {selectedData?.map((item) => (
              <TabPane tabId={item.id} key={item.id}>
                <Card className="mb-4 border">
                  <CardBody>
                    {/* <div className="d-flex align-items-center mb-3">
                      <img
                        src={`${imageBaseApi}${
                          editForms[item.id]?.mediaUrl || item?.media?.url || ""
                        }`}
                        alt=""
                        style={{
                          width: "80px",
                          height: "60px",
                          objectFit: "cover",
                          borderRadius: "6px",
                        }}
                        className="me-3"
                      />
                      <h6 className="mb-0 fw-bold">
                        {item.title || "Untitled Section"}
                      </h6>
                    </div> */}

                    <Row>
                      <Col md={4}>
                        <div className="mb-4">
                          <Label>Featured Media</Label>
                          {editForms[item.id]?.mediaId ? (
                            <div className="text-center p-3 border rounded shadow-sm bg-light">
                              <img
                                src={`${imageBaseApi}${
                                  editForms[item.id]?.mediaUrl ||
                                  item?.media?.url ||
                                  ""
                                }`}
                                alt="Featured"
                                style={{
                                  width: "100%",
                                  maxHeight: "220px",
                                  objectFit: "cover",
                                  borderRadius: "10px",
                                  border: "1px solid #ddd",
                                }}
                                className="mb-3"
                              />
                              <div className="d-flex gap-2 justify-content-center">
                                <Button
                                  size="sm"
                                  outline
                                  color="primary"
                                  onClick={() => setIsMediaOpen(item.id)}
                                >
                                  Change
                                </Button>
                                <Button
                                  size="sm"
                                  outline
                                  color="danger"
                                  onClick={() =>
                                    handleMediaChange(item.id, null)
                                  }
                                >
                                  Remove
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <div className="p-4 border rounded text-center bg-light">
                              <i className="ri-image-add-line fs-1 text-secondary"></i>
                              <h6 className="mt-2 mb-3 text-muted">
                                No banner image selected
                              </h6>
                              <h6 className="mb-3 text-muted">
                                1200 x 630
                              </h6>

                              <Button
                                outline
                                color="secondary"
                                onClick={() => setIsMediaOpen(item.id)}
                              >
                                Choose Featured Image
                              </Button>
                            </div>
                          )}
                        </div>
                      </Col>

                      <Col md={8}>
                        <FormGroup>
                          <Label>Title</Label>
                          <Input
                            type="text"
                            value={editForms[item.id]?.title || ""}
                            onChange={(e) =>
                              handleFormChange(item.id, "title", e.target.value)
                            }
                          />
                        </FormGroup>
                        <FormGroup>
                          <Label>Title Color</Label>
                          <ColorPicker
                            value={editForms[item.id]?.titleColor || "#0000"}
                            onChange={(color) =>
                              handleFormChange(item.id, "titleColor", color)
                            }
                          />
                        </FormGroup>
                        <FormGroup>
                          <Label>Sub title</Label>
                          <Input
                            type="text"
                            value={editForms[item.id]?.subTitle || ""}
                            onChange={(e) =>
                              handleFormChange(
                                item.id,
                                "subTitle",
                                e.target.value
                              )
                            }
                          />
                        </FormGroup>
                        <FormGroup switch>
                          <Input
                            type="switch"
                            checked={editForms[item.id]?.hasButton || false}
                            onChange={(e) => {
                              handleFormChange(
                                item.id,
                                "hasButton",
                                e.target.checked
                              ); // use checked
                            }}
                          />
                          <Label check>Has Button?</Label>
                        </FormGroup>

                        {editForms[item.id]?.hasButton && (
                          <>
                            <FormGroup>
                              <Label>Button Text</Label>
                              <Input
                                type="text"
                                value={editForms[item.id]?.buttonText || ""}
                                onChange={(e) =>
                                  handleFormChange(
                                    item.id,
                                    "buttonText",
                                    e.target.value
                                  )
                                }
                              />
                            </FormGroup>

                            <FormGroup>
                              <Label>Button URL</Label>
                              <Input
                                type="text"
                                value={editForms[item.id]?.buttonUrl || ""}
                                onChange={(e) =>
                                  handleFormChange(
                                    item.id,
                                    "buttonUrl",
                                    e.target.value
                                  )
                                }
                              />
                            </FormGroup>
                            <FormGroup>
                              <Label>Button Color</Label>
                              <ColorPicker
                                value={
                                  editForms[item.id]?.buttonColor ||
                                  "rgba(3, 142, 220, 1)"
                                }
                                onChange={(color) =>
                                  handleFormChange(
                                    item.id,
                                    "buttonColor",
                                    color
                                  )
                                }
                              />
                            </FormGroup>
                          </>
                        )}
                      </Col>
                    </Row>
                  </CardBody>
                </Card>

                <MediaModal
                  isOpen={isMediaOpen === item.id}
                  toggle={() => setIsMediaOpen(null)}
                  selectedMedia={
                    editForms[item.id]?.mediaId
                      ? {
                          url: editForms[item.id].mediaUrl,
                          name: editForms[item.id].mediaName,
                          size: editForms[item.id].mediaSize,
                        }
                      : null
                  }
                  setSelectedMedia={(media) =>
                    handleMediaChange(item.id, media)
                  }
                />
              </TabPane>
            ))}
          </TabContent>
        </ModalBody>

        <ModalFooter>
          <Button color="secondary" onClick={() => setIsOpenBulkEdit(false)}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleSaveAll}>
            Save All Changes
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default BannerSection;
